import { TEMPLATE_MSG, type Estimate, type EstimateTemplate } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { Link, useParams } from "react-router";
import { api, messageOf } from "../api.js";
import { ErrorText } from "../components/ui.jsx";
import { todayJst } from "../lib/num.js";
import { Editor, type TemplateBackend } from "./Estimate.jsx";

/**
 * 設定 → 見積書の雛形 → 雛形の編集（023。#197、docs/78）。
 * 画面は見積のエディタ（Estimate.tsx の Editor）をそのまま使い（同じ表・列の幅・行の編集の帯・候補の引き出し）、保存先だけ雛形にする。
 * 設定の2列に入れず全幅で、左のメニューは畳んで始める（app.tsx の Route の lineEntry）。戻るは「‹ 見積書の雛形」。
 * タイトルは紙の上の欄（見積の工事名の場所）。紙の備考は表の下の欄（見積書と同じ）。金額は見積と同じ DB の計算で付いて返る
 */

/** 雛形 → エディタが読む形（Estimate）。顧客・番号・日付は雛形に無いので、画面に出ない値で埋める */
function toEstimate(t: EstimateTemplate): Estimate {
  return {
    id: t.id,
    documentNo: null,
    status: "draft",
    client: { id: "00000000-0000-0000-0000-000000000000", name: "", honorific: "", branch: null, contact: null },
    projectGroup: null,
    revision: {
      id: t.id,
      no: 1,
      subject: t.title,
      issueDate: todayJst(),
      siteAddress: "",
      notes: t.notes,
      workPeriodText: "",
      workOutline: "",
      validityText: "",
      staffName: "",
      revisionNote: "",
      replaceSentence: "",
      lineRoundingMode: t.calc.lineRoundingMode,
      roundingMode: t.calc.roundingMode,
      subtotal: t.calc.subtotal,
      taxTotal: t.calc.taxTotal,
      reimbursementTotal: t.calc.reimbursementTotal,
      total: t.calc.total,
      taxPointDate: null,
    },
    lines: t.lines,
    sheets: t.sheets,
    issued: null,
    canUnissue: false,
    displayNo: null,
    canRevise: false,
    historyEnabled: false,
    revising: null,
    history: [],
    taxes: t.calc.taxes,
    updatedAt: t.updatedAt,
  };
}

export function TemplateEditPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["template", id], queryFn: () => api<EstimateTemplate>("GET", `/estimate-templates/${id}`), retry: false });
  const backend: TemplateBackend = useMemo(
    () => ({
      save: async (b) => {
        const t = await api<EstimateTemplate>("PUT", `/estimate-templates/${id}`, { title: b.subject, notes: b.notes, sheets: b.sheets, lines: b.lines });
        qc.setQueryData(["template", id], t);
        void qc.invalidateQueries({ queryKey: ["templates"] });
        return toEstimate(t);
      },
    }),
    [id, qc],
  );
  if (q.isPending) return <p className="hint">読み込んでいます…</p>;
  if (q.isError) {
    return (
      <div className="soonbox">
        <ErrorText>{messageOf(q.error)}</ErrorText>
        <p>
          <Link className="btn text" to="/settings/templates">{TEMPLATE_MSG.backToList}</Link>
        </p>
      </div>
    );
  }
  return <Editor key={q.data.id} initial={toEstimate(q.data)} template={backend} />;
}
